import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Truck, Info, FileText } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { resolveImg } from "@/components/ProductCard";
import { FdaDisclaimer, ShippingRestriction } from "@/components/Compliance";
import { useCart } from "@/context/CartContext";
import { FLOWER_SIZES, PREROLL_OPTIONS, ALL_FLOWER_TIERS, SIZE_NOTE, isFlower, glowClass } from "@/lib/sizes";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      if (data.category === "flower") {
        const first = ALL_FLOWER_TIERS.find((s) => (data.prices || {})[s.key] > 0);
        setSize(first?.key || null);
      }
    }).catch(() => navigate("/"));
  }, [id, navigate]);

  if (!product) return <p className="py-40 text-center text-neutral-500">Loading…</p>;

  const flower = isFlower(product);
  const prices = product.prices || {};
  const availableSizes = FLOWER_SIZES.filter((s) => prices[s.key] > 0);
  const availablePrerolls = PREROLL_OPTIONS.filter((s) => prices[s.key] > 0);
  const unitPrice = flower ? prices[size] || 0 : product.price || 0;
  const coaImgUrl = (item) => (typeof item === "string" ? item : item?.url);
  const firstCoa = (product.coa_images || [])[0];
  const coaUrl = firstCoa
    ? resolveImg(coaImgUrl(firstCoa))
    : `${window.location.origin}/product/${product.id}`;

  const handleAdd = () => {
    if (flower) {
      if (!size) {
        toast.error("Please select a size");
        return;
      }
      const sizeMeta = ALL_FLOWER_TIERS.find((s) => s.key === size);
      addItem(product, qty, {
        size,
        sizeLabel: `${sizeMeta.label} (${sizeMeta.grams})`,
        unitPrice,
      });
    } else {
      addItem(product, qty);
    }
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <Link
        to="/shop"
        data-testid="back-link"
        className="mb-10 inline-flex items-center gap-2 text-sm text-neutral-600 transition-colors hover:text-[#a06d34]"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to shop
      </Link>

      <div className="grid gap-12 lg:grid-cols-2">
        <div className="border border-black/10 bg-[#f2ece1]">
          <img
            src={resolveImg(product.image_url)}
            alt={product.name}
            className="aspect-square w-full object-cover"
          />
        </div>

        <div className="flex flex-col justify-center">
          <div className="mb-3 flex items-center gap-3">
            <span className="overline">{product.category}</span>
            {product.strain_type && (
              <span
                className={`border px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#2b2520] ${glowClass(product.strain_type)}`}
              >
                {product.strain_type}
              </span>
            )}
            {flower && product.grow_method && (
              <span
                data-testid="detail-grow-method"
                className="border border-[#6ba9cd]/50 bg-[#9ecbe4]/20 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#2b6a8c]"
              >
                {product.grow_method}
              </span>
            )}
          </div>
          <h1 className="mb-4 text-4xl font-black leading-none text-[#2b2520] sm:text-5xl">
            {product.name}
          </h1>
          <div className="mb-6 flex flex-wrap items-center gap-6 text-sm text-neutral-600">
            {flower ? (
              <>
                {product.thca ? <span data-testid="detail-thca">{product.thca}% THCa</span> : null}
                {product.d9 ? <span data-testid="detail-d9">{product.d9}% D9</span> : null}
                <span>Multiple sizes available</span>
              </>
            ) : (
              <>
                <span>{product.thca}% THCA</span>
                <span>{(product.stock ?? 0) > 0 ? `${product.stock} in stock` : "Sold out"}</span>
              </>
            )}
          </div>
          <p className="mb-8 max-w-lg text-neutral-700">{product.description}</p>

          {flower && (product.terpenes || []).length > 0 && (
            <div className="mb-8" data-testid="detail-terpenes">
              <p className="overline mb-3">Top terpenes</p>
              <div className="flex flex-wrap gap-2">
                {product.terpenes.map((t, i) => (
                  <span
                    key={i}
                    data-testid={`terpene-${i}`}
                    className="border border-[#c99a63]/50 bg-[#c99a63]/10 px-3 py-1.5 text-xs font-medium text-[#2b2520]"
                  >
                    {t.name}
                    {t.percentage ? <span className="ml-1 text-[#a06d34]">{t.percentage}%</span> : null}
                  </span>
                ))}
              </div>
            </div>
          )}

          {flower ? (
            <div className="mb-6">
              {availableSizes.length > 0 && (
                <>
                  <p className="overline mb-3">Select size</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="size-selector">
                    {availableSizes.map((s) => (
                      <button
                        key={s.key}
                        data-testid={`size-${s.key}`}
                        onClick={() => setSize(s.key)}
                        className={`flex flex-col items-start border px-4 py-3 text-left transition-colors duration-300 ${
                          size === s.key
                            ? "border-[#c99a63] bg-[#c99a63]/15"
                            : "border-black/15 hover:border-[#c99a63]/60"
                        }`}
                      >
                        <span className="text-sm font-bold text-[#2b2520]">{s.label}</span>
                        <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                          {s.grams}
                        </span>
                        <span className="mt-1 text-sm text-[#a06d34]">
                          ${prices[s.key].toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 flex items-start gap-2 border border-black/10 bg-white p-3 text-xs text-neutral-600" data-testid="size-note">
                    <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#a06d34]" strokeWidth={1.5} />
                    {SIZE_NOTE}
                  </div>
                </>
              )}

              {availablePrerolls.length > 0 && (
                <div className="mt-6" data-testid="preroll-selector">
                  <p className="overline mb-3">Prerolls</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {availablePrerolls.map((s) => (
                      <button
                        key={s.key}
                        data-testid={`size-${s.key}`}
                        onClick={() => setSize(s.key)}
                        className={`flex flex-col items-start border px-4 py-3 text-left transition-colors duration-300 ${
                          size === s.key
                            ? "border-[#c99a63] bg-[#c99a63]/15"
                            : "border-black/15 hover:border-[#c99a63]/60"
                        }`}
                      >
                        <span className="text-sm font-bold text-[#2b2520]">{s.label}</span>
                        <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                          {s.grams}
                        </span>
                        <span className="mt-1 text-sm text-[#a06d34]">
                          ${prices[s.key].toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <p className="mb-8 text-3xl font-semibold text-[#2b2520]" data-testid="detail-price">
            ${unitPrice.toFixed(2)}
          </p>

          <div className="mb-6 flex items-center gap-4">
            <div className="flex items-center border border-black/15">
              <button
                data-testid="qty-decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-4 py-3 text-[#2b2520] hover:text-[#a06d34]"
              >
                <Minus className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <span data-testid="qty-value" className="w-10 text-center text-[#2b2520]">
                {qty}
              </span>
              <button
                data-testid="qty-increase"
                onClick={() => setQty((q) => q + 1)}
                className="px-4 py-3 text-[#2b2520] hover:text-[#a06d34]"
              >
                <Plus className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={handleAdd}
              className="flex-1 bg-[#c99a63] py-4 text-sm font-semibold uppercase tracking-widest text-[#2b2520] transition-colors duration-300 hover:bg-[#bb8850]"
            >
              Add to cart
            </button>
          </div>

          <div className="flex items-center gap-2 border-t border-black/10 pt-6 text-sm text-neutral-600">
            <Truck className="h-4 w-4 text-[#a06d34]" strokeWidth={1.5} />
            Discreet shipping. Age verification required at checkout.
          </div>

          {((product.coa_images || []).length > 0 || product.coa_batch_number || product.coa_lab_name || product.coa_test_date) && (
            <div className="mt-6 border-t border-black/10 pt-6" data-testid="detail-coa">
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#a06d34]" strokeWidth={1.5} />
                <p className="overline">Certificate of Analysis</p>
              </div>
              <div className="space-y-1 text-sm text-neutral-600">
                <p>
                  For <span className="font-medium text-[#2b2520]">{product.name}</span>
                </p>
                {product.coa_batch_number && (
                  <p data-testid="coa-batch">Batch #: <span className="font-mono text-[#2b2520]">{product.coa_batch_number}</span></p>
                )}
                {product.coa_lab_name && <p>Lab: {product.coa_lab_name}</p>}
                {product.coa_test_date && <p>Tested: {product.coa_test_date}</p>}
              </div>
              {(product.coa_images || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {product.coa_images.map((item, i) => (
                    <a
                      key={i}
                      href={resolveImg(coaImgUrl(item))}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`view-coa-${i}`}
                      className="block w-28 border border-black/10 transition-colors hover:border-[#c99a63]"
                    >
                      <img src={resolveImg(coaImgUrl(item))} alt={`COA ${i + 1}`} className="h-28 w-28 object-cover" />
                      {typeof item === "object" && item?.name && (
                        <p className="truncate px-1 py-1 text-[10px] text-neutral-600" title={item.name}>{item.name}</p>
                      )}
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-center gap-3" data-testid="coa-qr">
                <div className="border border-black/10 bg-white p-2">
                  <QRCodeCanvas value={coaUrl} size={88} fgColor="#2b2520" bgColor="#ffffff" level="M" />
                </div>
                <p className="max-w-[12rem] text-xs text-neutral-500">
                  Scan to view this product's Certificate of Analysis on any device.
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 space-y-3 border-t border-black/10 pt-6" data-testid="product-compliance">
            <ShippingRestriction />
            <FdaDisclaimer />
          </div>
        </div>
      </div>
    </div>
  );
}
